import { useState } from 'react';
import UseAiCountButton from '../UseAiCountButton/UseAiCountButton';

export default function NumberStepper({
    label,
    value,
    onChange,
    onDecrement,
    onIncrement,
    required,
    disabled = false,
    loading = false,
    inputMode = 'numeric',
    formatDisplay = (v) => String(v),
    getEditValue = (v) => String(v),
    parseInput = (text) => Number(text),
    helperText = null,
    aiSuggestion = null,
    onUseAiSuggestion = null,
}) {
    const [draft, setDraft] = useState(null);
    const editing = draft !== null;
    const shouldBeDisabled = disabled || loading;
    const displayValue = loading
        ? 'Loading…'
        : (editing ? draft : formatDisplay(value));

    const commitDraft = () => {
        const parsed = parseInput(draft ?? '');
        if (!Number.isNaN(parsed)) {
            onChange(parsed);
        }
        setDraft(null);
    };

    return (
        <div className="flex w-full flex-col gap-1.5">
            <p className="shrink-0 text-sm font-medium leading-snug text-gray-900">
                {label}
                {required && <span className="text-primary"> *</span>}
            </p>
            <div className="flex items-center gap-0 overflow-hidden w-full rounded-lg border border-gray-300">
                <button
                    type="button"
                    onClick={onDecrement}
                    disabled={shouldBeDisabled}
                    className="bg-gray-100 px-4 py-3 text-lg font-medium text-gray-600 transition-colors hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
                    aria-label={`Decrease ${label}`}
                >
                    −
                </button>
                <input
                    type="text"
                    inputMode={inputMode}
                    value={displayValue}
                    onFocus={() => setDraft(getEditValue(value))}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitDraft}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.currentTarget.blur();
                        }
                    }}
                    disabled={shouldBeDisabled}
                    aria-label={label}
                    className="min-w-0 flex-1 border-0 bg-white px-2 py-3 text-center text-base font-semibold text-gray-900 outline-none focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                />
                <button
                    type="button"
                    onClick={onIncrement}
                    disabled={shouldBeDisabled}
                    className="bg-gray-100 px-4 py-3 text-lg font-medium text-gray-600 transition-colors hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
                    aria-label={`Increase ${label}`}
                >
                    +
                </button>
            </div>
            {helperText && (
                <p className="text-xs leading-relaxed text-gray-500">{helperText}</p>
            )}
            {onUseAiSuggestion && (
                <div className="self-start">
                    <UseAiCountButton
                        aiCount={aiSuggestion}
                        disabled={aiSuggestion == null}
                        onClick={onUseAiSuggestion}
                    />
                </div>
            )}
        </div>
    );
}
